/**
 * The active language, as an external store.
 *
 * Not React context. Dates, number formatting and the API's `Accept-Language`
 * header all need the current language, and only some of them are called from
 * inside a component. An external store gives one answer to both worlds, and
 * `useSyncExternalStore` keeps React honest about it without a provider that
 * everything has to be nested inside.
 */

export const LANGUAGES = ['en', 'cs', 'de'] as const;
export type Language = (typeof LANGUAGES)[number];

/** What each language calls itself — never translated. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  cs: 'Čeština',
  de: 'Deutsch',
};

/** BCP 47 tags for `Intl.*`. The stored value is the bare subtag. */
const INTL_LOCALES: Record<Language, string> = {
  en: 'en-GB',
  cs: 'cs-CZ',
  de: 'de-DE',
};

const STORAGE_KEY = 'mirondo.language';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * The best language for a first visit.
 *
 * A previous choice wins over the browser's list, because it *was* a choice.
 * The account's own preference arrives later, once the session is loaded, and
 * overrides both — see `useLanguageSync`.
 */
function detectLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // Safari in private mode throws on localStorage. Not worth failing over.
  }
  for (const tag of navigator.languages ?? [navigator.language]) {
    const primary = tag.split('-')[0]?.toLowerCase();
    if (isLanguage(primary)) return primary;
  }
  return 'en';
}

let current: Language = detectLanguage();
const listeners = new Set<() => void>();

export function getLanguage(): Language {
  return current;
}

/** The BCP 47 tag to hand to `Intl` and `toLocaleDateString`. */
export function getIntlLocale(): string {
  return INTL_LOCALES[current];
}

export function setLanguage(next: Language, { persist = true } = {}): void {
  if (next === current) return;
  current = next;
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // As above — a preference that cannot be saved is still usable now.
    }
  }
  document.documentElement.lang = next;
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// The initial value has to reach the DOM too: screen readers and the browser's
// own translation prompt both read `<html lang>`.
document.documentElement.lang = current;
